/**
 * Copyright (c) 2025 Répét Contributors
 * Licensed under the MIT License
 * See LICENSE file in the project root for full license text
 */

/**
 * Utilitaire de debug pour diagnostiquer les problèmes de mise à jour PWA
 *
 * Ce fichier fournit des outils pour :
 * - Inspecter l'état des Service Workers
 * - Lister les caches actifs
 * - Vérifier les versions
 * - Forcer des mises à jour manuelles
 */

import { VERSION_STORAGE_KEYS } from '../config/version'

/**
 * Interface pour les informations de Service Worker
 */
interface SWInfo {
  state: string
  scriptURL: string
  version?: string
}

/**
 * Interface pour les informations de cache
 */
interface CacheInfo {
  name: string
  size: number
  urls: string[]
}

/**
 * Récupère les informations sur tous les Service Workers
 */
export async function getServiceWorkerInfo(): Promise<{
  controller: SWInfo | null
  waiting: SWInfo | null
  active: SWInfo | null
  installing: SWInfo | null
  registration: boolean
}> {
  if (!('serviceWorker' in navigator)) {
    // console.warn('[PWA Debug] Service Worker non supporté')
    return {
      controller: null,
      waiting: null,
      active: null,
      installing: null,
      registration: false,
    }
  }

  const registration = await navigator.serviceWorker.getRegistration()

  if (!registration) {
    // console.warn('[PWA Debug] Aucun Service Worker enregistré')
    return {
      controller: null,
      waiting: null,
      active: null,
      installing: null,
      registration: false,
    }
  }

  const info = {
    controller: navigator.serviceWorker.controller
      ? {
          state: navigator.serviceWorker.controller.state,
          scriptURL: navigator.serviceWorker.controller.scriptURL,
        }
      : null,
    waiting: registration.waiting
      ? {
          state: registration.waiting.state,
          scriptURL: registration.waiting.scriptURL,
        }
      : null,
    active: registration.active
      ? {
          state: registration.active.state,
          scriptURL: registration.active.scriptURL,
        }
      : null,
    installing: registration.installing
      ? {
          state: registration.installing.state,
          scriptURL: registration.installing.scriptURL,
        }
      : null,
    registration: true,
  }

  return info
}

/**
 * Liste tous les caches et leur contenu
 */
export async function listCaches(): Promise<CacheInfo[]> {
  if (!('caches' in window)) {
    // console.warn('[PWA Debug] Cache Storage non supporté')
    return []
  }

  const cacheNames = await caches.keys()
  const cacheInfos: CacheInfo[] = []

  for (const cacheName of cacheNames) {
    const cache = await caches.open(cacheName)
    const keys = await cache.keys()

    cacheInfos.push({
      name: cacheName,
      size: keys.length,
      urls: keys.map((req) => req.url),
    })
  }

  return cacheInfos
}

/**
 * Supprime tous les caches (utile pour debug)
 */
export async function clearAllCaches(): Promise<number> {
  if (!('caches' in window)) {
    // console.warn('[PWA Debug] Cache Storage non supporté')
    return 0
  }

  const cacheNames = await caches.keys()
  // console.warn(`[PWA Debug] Suppression de ${cacheNames.length} cache(s)...`)

  await Promise.all(cacheNames.map((name) => caches.delete(name)))

  // console.warn('[PWA Debug] ✅ Tous les caches supprimés')
  return cacheNames.length
}

/**
 * Désinscrit tous les Service Workers
 */
export async function unregisterAllServiceWorkers(): Promise<number> {
  if (!('serviceWorker' in navigator)) {
    // console.warn('[PWA Debug] Service Worker non supporté')
    return 0
  }

  const registrations = await navigator.serviceWorker.getRegistrations()
  // console.warn(`[PWA Debug] Désinscription de ${registrations.length} Service Worker(s)...`)

  await Promise.all(registrations.map((reg) => reg.unregister()))

  // console.warn('[PWA Debug] ✅ Tous les Service Workers désinscits')
  return registrations.length
}

/**
 * Réinitialise complètement la PWA (caches + SW + localStorage)
 */
export async function resetPWA(): Promise<void> {
  // console.warn('[PWA Debug] 🔄 Réinitialisation complète de la PWA...')

  // 1. Supprimer les caches
  await clearAllCaches()
  // console.warn(`[PWA Debug] - ${_cacheCount} cache(s) supprimé(s)`)

  // 2. Désinscire les Service Workers
  await unregisterAllServiceWorkers()
  // console.warn(`[PWA Debug] - ${_swCount} Service Worker(s) désinscrit(s)`)

  // 3. Nettoyer le localStorage des versions
  Object.values(VERSION_STORAGE_KEYS).forEach((key) => {
    localStorage.removeItem(key)
  })
  // console.warn('[PWA Debug] - localStorage nettoyé')

  // console.warn('[PWA Debug] ✅ Réinitialisation terminée - Rechargement...')

  // 4. Recharger la page
  setTimeout(() => {
    window.location.reload()
  }, 1000)
}

/**
 * Supprimer complètement la base de données IndexedDB
 * Utile pour réinitialiser l'application lors des tests
 */
export async function deleteDatabase(): Promise<void> {
  // console.warn('[PWA Debug] 🗑️ Suppression de la base de données IndexedDB...')

  try {
    // Import dynamique pour éviter les dépendances circulaires
    const { db } = await import('../core/storage/database')

    // Fermer la connexion
    db.close()
    // console.warn('[PWA Debug] - Connexion fermée')

    // Supprimer la base de données
    await db.delete()
    // console.warn('[PWA Debug] - Base de données supprimée')

    // Nettoyer l'état de synchronisation dans localStorage
    // Sinon le client essaiera de sync depuis le dernier timestamp au lieu de tout récupérer
    localStorage.removeItem('lastSyncAt')
    localStorage.removeItem('deviceId')
    // console.warn('[PWA Debug] - État de synchronisation réinitialisé')

    // console.warn('[PWA Debug] ✅ Suppression terminée - Rechargez la page pour réinitialiser')
    // console.warn('[PWA Debug] ⚠️ Utilisez location.reload() pour recharger')
  } catch (error) {
    console.error('[PWA Debug] ❌ Erreur lors de la suppression de la base de données:', error)
    throw error
  }
}

/**
 * Forcer la vérification de mise à jour
 */
export async function checkForUpdates(): Promise<boolean> {
  if (!('serviceWorker' in navigator)) {
    // console.warn('[PWA Debug] Service Worker non supporté')
    return false
  }

  const registration = await navigator.serviceWorker.getRegistration()

  if (!registration) {
    // console.warn('[PWA Debug] Aucun Service Worker enregistré')
    return false
  }

  // console.warn('[PWA Debug] Vérification manuelle des mises à jour...')
  await registration.update()

  // Attendre un peu pour voir si un nouveau SW apparaît
  await new Promise((resolve) => setTimeout(resolve, 2000))

  const newRegistration = await navigator.serviceWorker.getRegistration()

  if (newRegistration?.waiting) {
    // console.warn('[PWA Debug] ✅ Nouvelle version détectée (waiting)')
    return true
  }

  if (newRegistration?.installing) {
    // console.warn("[PWA Debug] ✅ Nouvelle version en cours d'installation")
    return true
  }

  // console.warn('[PWA Debug] Aucune mise à jour disponible')
  return false
}

/**
 * Active un Service Worker en attente
 */
export async function activateWaitingSW(): Promise<boolean> {
  if (!('serviceWorker' in navigator)) {
    // console.warn('[PWA Debug] Service Worker non supporté')
    return false
  }

  const registration = await navigator.serviceWorker.getRegistration()

  if (!registration?.waiting) {
    // console.warn('[PWA Debug] Aucun Service Worker en attente')
    return false
  }

  // console.warn('[PWA Debug] Activation du Service Worker en attente...')
  registration.waiting.postMessage({ type: 'SKIP_WAITING' })

  // Attendre l'activation
  await new Promise((resolve) => setTimeout(resolve, 500))

  // console.warn('[PWA Debug] ✅ Activation demandée - Rechargement...')
  window.location.reload()

  return true
}

/**
 * Affiche un rapport complet de debug dans la console
 */
export async function printDebugReport(): Promise<void> {
  // console.warn('═'.repeat(60))
  // console.warn('📊 RAPPORT DE DEBUG PWA')
  // console.warn('═'.repeat(60))

  // Version de l'app
  // console.warn('\n📦 VERSION')
  // console.warn(`App Version: ${APP_VERSION}`)
  // console.warn(`Stored Version: ${localStorage.getItem(VERSION_STORAGE_KEYS.APP_VERSION) || 'N/A'}`)

  // Service Workers
  // console.warn('\n🔧 SERVICE WORKERS')
  await getServiceWorkerInfo()
  // console.warn('Registration:', swInfo.registration)
  // console.warn('Controller:', swInfo.controller)
  // console.warn('Active:', swInfo.active)
  // console.warn('Waiting:', swInfo.waiting)
  // console.warn('Installing:', swInfo.installing)

  // Caches
  // console.warn('\n💾 CACHES')
  const caches = await listCaches()
  // console.warn(`Total: ${caches.length} cache(s)`)
  caches.forEach((_cache) => {
    // console.warn(`- ${_cache.name}: ${_cache.size} entrée(s)`)
  })

  // Détails des caches (limité aux 5 premiers éléments par cache)
  // console.warn('\n📋 DÉTAILS DES CACHES (5 premiers éléments)')
  caches.forEach((_cache) => {
    // console.warn(`\n${_cache.name}:`)
    _cache.urls.slice(0, 5).forEach((_url) => {
      // console.warn(`  - ${_url}`)
    })
    if (_cache.urls.length > 5) {
      // console.warn(`  ... et ${_cache.urls.length - 5} de plus`)
    }
  })

  // console.warn('\n═'.repeat(60))
  // console.warn('Pour réinitialiser complètement la PWA:')
  // console.warn('  import { resetPWA } from "@/utils/pwa-debug"')
  // console.warn('  resetPWA()')
  // console.warn('═'.repeat(60))
}

/**
 * Interface pour window.pwaDebug
 */
interface PWADebugAPI {
  getServiceWorkerInfo: typeof getServiceWorkerInfo
  listCaches: typeof listCaches
  clearAllCaches: typeof clearAllCaches
  unregisterAllServiceWorkers: typeof unregisterAllServiceWorkers
  resetPWA: typeof resetPWA
  deleteDatabase: typeof deleteDatabase
  checkForUpdates: typeof checkForUpdates
  activateWaitingSW: typeof activateWaitingSW
  printDebugReport: typeof printDebugReport
}

/**
 * Augmentation de l'interface Window
 */
declare global {
  interface Window {
    pwaDebug?: PWADebugAPI
  }
}

/**
 * Expose les fonctions de debug dans window (pour accès console)
 */
export function enablePWADebug(): void {
  if (typeof window === 'undefined') return

  window.pwaDebug = {
    getServiceWorkerInfo,
    listCaches,
    clearAllCaches,
    unregisterAllServiceWorkers,
    resetPWA,
    deleteDatabase,
    checkForUpdates,
    activateWaitingSW,
    printDebugReport,
  }

  // console.warn('🔍 PWA Debug activé - Utilisez window.pwaDebug pour accéder aux outils')
  // console.warn('Exemple: window.pwaDebug.printDebugReport()')
}
